import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { useSegmentIdentify } from '@redbubble/react-segment';
import DesignSystemProvider from '@redbubble/design-system/react/DesignSystemProvider';
import defaultTheme from '@redbubble/design-system/react/themes/default';
import '@redbubble/cookie-banner/lib/styles.css';
import '@redbubble/design-system/react/Text';
import config from '../config';
import styles from './App.css';
import Routing from './components/Routing';
import Head from './components/Head';
import Header from './components/Header';
import Footer from './components/Footer';
import {
  FeatureToggleContext,
  extractFeaturesFromLocation
} from './contexts/FeatureToggle';
import {
  ExperimentContext,
  computeExperimentFromFederatedId
} from './contexts/Experiment';
import SignUpModalProvider from './contexts/SignUpModal';
import { MarketingFooter } from './components/MarketingFooter/MarketingFooter';
import { getRedirectUrl } from './lib/routing/routing';

const DEFAULT_LOCALE = config('defaultLocale');

export const App = ({
  cookies,
  userInfo,
  location,
  analyticsData,
  userAccount,
  rbVisitorID
}) => {
  const featureToggles = useMemo(
    () => extractFeaturesFromLocation({ location }),
    [location]
  );
  const experiment = useMemo(
    () => computeExperimentFromFederatedId(analyticsData.federatedId),
    [analyticsData]
  );

  useSegmentIdentify({
    userId: get(userInfo, 'federatedId'),
    locale: get(userInfo, 'locale', 'en'),
    isLoggedIn: get(userInfo, 'isLoggedIn', false)
  });

  const redirectUrl = getRedirectUrl(userInfo.locale, location);

  return (
    <FeatureToggleContext.Provider value={featureToggles}>
      <ExperimentContext.Provider value={experiment}>
        <DesignSystemProvider
          className={styles.dsWrapper}
          config={{
            theme: defaultTheme,
            useToasts: true
          }}
        >
          <SignUpModalProvider>
            <div className={styles.app}>
              <Head
                locale={userInfo.locale}
                userInfo={userInfo}
                cookies={cookies}
                userAccount={userAccount}
                rbVisitorID={rbVisitorID}
              />

              <Header userInfo={userInfo} redirectUrl={redirectUrl} />

              <Routing cookies={cookies.getAll()} userInfo={userInfo} />
              <MarketingFooter />
              <Footer
                userInfo={{ country: '', currency: '', ...userInfo }}
                redirectUrl={redirectUrl}
              />
            </div>
          </SignUpModalProvider>
        </DesignSystemProvider>
      </ExperimentContext.Provider>
    </FeatureToggleContext.Provider>
  );
};

App.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  userInfo: PropTypes.shape({
    locale: PropTypes.string
  }),
  cookies: PropTypes.shape({}),
  analyticsData: PropTypes.shape({
    federatedId: PropTypes.string
  })
};

App.defaultProps = {
  userInfo: {
    locale: DEFAULT_LOCALE
  },
  cookies: {},
  analyticsData: {}
};

export default App;
